import {
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { useCallback, useState } from "react";

import { db } from "integrations/firebase/firestore";

export type EntityFields = Record<string, unknown>;

/** What one delete came to, with the reason when it did not go through. */
export type DeleteOutcome = { ok: true } | { ok: false; error: string };

/**
 * Deletes one document of an event content subcollection and says why when it
 * could not. The single delete buttons and the multi-select delete both go
 * through this, so a refusal reads the same in both.
 */
export async function deleteEntityDoc(
  eventId: string,
  collectionName: string,
  id: string,
): Promise<DeleteOutcome> {
  try {
    await deleteDoc(doc(db, "events", eventId, collectionName, id));
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Failed to delete",
    };
  }
}

/**
 * Create, update and direct-delete for an event content subcollection.
 *
 * Two rules of the write contract are enforced here rather than left to each
 * caller, because getting either wrong produces an opaque permission-denied:
 *
 *  - Timestamps are pinned to `request.time`, so they must be the server
 *    sentinel. A client clock is always refused.
 *  - Updates are restricted by `affectedKeys`, and `createdAt` is in no
 *    update list. Spreading a loaded document into an update therefore fails
 *    even when nothing about it changed, so `update` takes only the fields
 *    that are actually being written.
 */
export function useEntityWrites(eventId: string, collectionName: string) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const path = useCallback(
    () => collection(db, "events", eventId, collectionName),
    [eventId, collectionName],
  );

  const create = useCallback(
    async (
      fields: EntityFields,
      options?: { timestampFields?: string[]; id?: string },
    ): Promise<string | null> => {
      setSaving(true);
      setError(null);
      try {
        const ref = options?.id
          ? doc(db, "events", eventId, collectionName, options.id)
          : doc(path());
        const stamps = Object.fromEntries(
          (options?.timestampFields ?? ["createdAt"]).map((field) => [
            field,
            serverTimestamp(),
          ]),
        );
        await setDoc(ref, { ...fields, ...stamps });
        return ref.id;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to save");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [eventId, collectionName, path],
  );

  const update = useCallback(
    async (
      id: string,
      fields: EntityFields,
      options?: { timestampFields?: string[] },
    ): Promise<boolean> => {
      setSaving(true);
      setError(null);
      try {
        const stamps = Object.fromEntries(
          (options?.timestampFields ?? []).map((field) => [
            field,
            serverTimestamp(),
          ]),
        );
        await updateDoc(doc(db, "events", eventId, collectionName, id), {
          ...fields,
          ...stamps,
        });
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to save");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [eventId, collectionName],
  );

  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      setSaving(true);
      setError(null);
      const outcome = await deleteEntityDoc(eventId, collectionName, id);
      if (!outcome.ok) setError(outcome.error);
      setSaving(false);
      return outcome.ok;
    },
    [eventId, collectionName],
  );

  return {
    create,
    update,
    remove,
    saving,
    error,
    clearError: useCallback(() => setError(null), []),
  };
}
